import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { fetchAdConfig } from '../services/ads/adConfigService.js';
import { loadPayPalSdk } from '../services/commerce/commerceService.js';
import {
  captureAdvertisingOrder,
  createAdvertisingOrder,
  fetchAdvertiserReport,
  submitAdvertisingCampaign,
} from '../services/business/businessService.js';
import '../styles/business.css';

const ADVERTISING_EMAIL = 'advertise@watchvim.com';

const FALLBACK_OFFERS = {
  currency: 'USD',
  cpm: { '15': '20.00', '30': '30.00' },
  flatPackages: [
    { key: 'starter', label: 'Starter', impressions: 5000, prices: { '15': '100.00', '30': '150.00' } },
    { key: 'standard', label: 'Standard', impressions: 10000, prices: { '15': '200.00', '30': '300.00' } },
    { key: 'growth', label: 'Growth', impressions: 25000, prices: { '15': '475.00', '30': '700.00' } },
    { key: 'reach', label: 'Reach', impressions: 50000, prices: { '15': '900.00', '30': '1300.00' } },
  ],
  daily: {
    minBudget: { '15': '20.00', '30': '30.00' },
    maxBudget: '5000.00',
    minDays: 1,
    maxDays: 30,
    defaultDays: 7,
  },
};

function BusinessShell({ eyebrow = 'WatchVIM', title, subtitle, children, className = '' }) {
  return (
    <section className={`business-page ${className}`}>
      <header className="business-hero">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </header>
      <div className="business-content">{children}</div>
    </section>
  );
}

function StatusMessage({ error, success, status }) {
  if (error) return <p className="business-message error" role="alert">{error}</p>;
  if (success) return <p className="business-message success" role="status">{success}</p>;
  if (status) return <p className="business-message" role="status">{status}</p>;
  return null;
}

function BusinessNav() {
  return (
    <nav className="business-subnav" aria-label="WatchVIM advertising">
      <NavLink to="/advertise">Overview</NavLink>
      <NavLink to="/advertise/video">Video Ads</NavLink>
      <NavLink to="/advertise/pricing">Pricing</NavLink>
      <NavLink to="/advertise/dashboard">Campaign Report</NavLink>
      <NavLink to="/advertise/display">Display</NavLink>
      <NavLink to="/advertise/sponsorships">Sponsorships</NavLink>
    </nav>
  );
}

function formatInt(value) {
  return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(number) ? 0 : 2 });
}

function packageLink(pkg, duration) {
  return `/advertise/video?mode=flat&package=${encodeURIComponent(pkg.key)}&duration=${duration}`;
}

export function AdvertisePage({ section = 'overview' }) {
  const [config, setConfig] = useState(null);
  useEffect(() => { fetchAdConfig().then(setConfig).catch(() => {}); }, []);
  const offers = config?.advertiserOffers || FALLBACK_OFFERS;

  const heading = {
    overview: ['Put Your Brand in Front of WatchVIM Viewers', 'Run 15- or 30-second video campaigns across eligible WatchVIM streaming inventory. Start at $100 for 5,000 guaranteed impressions.'],
    display: ['Display Advertising', 'Build visibility around the WatchVIM viewing experience with approved display and companion placements.'],
    sponsorships: ['WatchVIM Sponsorships', 'Build a larger brand presence around programming, premieres, collections, originals, and WatchVIM initiatives.'],
    pricing: ['Flexible Video Advertising', 'Choose a guaranteed impression package or set a daily budget. WatchVIM keeps serving the campaign until its purchased delivery goal is reached, subject to campaign approval and eligible inventory.'],
  }[section] || ['Advertise on WatchVIM', 'Reach WatchVIM viewers with measurable streaming campaigns.'];

  return (
    <BusinessShell eyebrow="WatchVIM for Brands" title={heading[0]} subtitle={heading[1]} className="advertise-page">
      <BusinessNav />

      {section === 'pricing' ? (
        <>
          <div className="business-card">
            <span className="business-kicker">Straightforward OTT pricing</span>
            <h2>Start at $100</h2>
            <p>A 15-second Starter campaign includes 5,000 guaranteed impressions. Pre-roll and mid-roll use the same base package pricing, so you can select the placement that fits your campaign.</p>
            <p className="business-muted">15-second base CPM: ${offers.cpm?.['15'] || '20.00'} · 30-second base CPM: ${offers.cpm?.['30'] || '30.00'}</p>
          </div>
          <div className="business-card-grid four">
            {(offers.flatPackages || FALLBACK_OFFERS.flatPackages).map((pkg) => (
              <article className="business-card pricing-card" key={pkg.key}>
                <span className="business-kicker">{pkg.label}</span>
                <h2>{formatInt(pkg.impressions)} impressions</h2>
                <p><strong>15 seconds:</strong> {money(pkg.prices?.['15'])}</p>
                <p><strong>30 seconds:</strong> {money(pkg.prices?.['30'])}</p>
                <div className="business-field-row two">
                  <NavLink className="business-primary" to={packageLink(pkg, '15')}>Buy 15s</NavLink>
                  <NavLink to={packageLink(pkg, '30')}>Buy 30s</NavLink>
                </div>
              </article>
            ))}
          </div>
          <div className="business-card-grid two">
            <article className="business-card">
              <span className="business-kicker">Daily budget</span>
              <h2>Choose how much to spend per day</h2>
              <p>Set a daily budget and a 1–30 day flight. At the base rate, $20/day delivers up to 1,000 15-second impressions per day and $30/day delivers up to 1,000 30-second impressions per day.</p>
              <NavLink className="business-primary" to="/advertise/video?mode=daily&duration=15">Build Daily Campaign</NavLink>
            </article>
            <article className="business-card">
              <span className="business-kicker">Where ads can run</span>
              <h2>Pre-roll + Mid-roll</h2>
              <p>Choose pre-roll, mid-roll, or both. Campaigns can be platform-wide or targeted to a published WatchVIM title. Eligible delivery can include WatchVIM web and supported OTT app inventory as those placements are enabled.</p>
              <NavLink to="/advertise/video">Build Campaign</NavLink>
            </article>
          </div>
        </>
      ) : section === 'display' ? (
        <div className="business-card-grid two">
          <article className="business-card">
            <h2>Display & Companion Placements</h2>
            <p>Use approved image creative around the WatchVIM viewing experience. Display inventory is quoted based on placement, dimensions, campaign dates, and available inventory.</p>
            <a className="business-primary" href={`mailto:${ADVERTISING_EMAIL}?subject=WatchVIM%20Display%20Advertising`}>Request Display Inventory</a>
          </article>
          <article className="business-card">
            <h2>Need video instead?</h2>
            <p>Self-service 15- and 30-second streaming campaigns start at $100 and include measurable impression delivery.</p>
            <NavLink to="/advertise/pricing">View Video Pricing</NavLink>
          </article>
        </div>
      ) : section === 'sponsorships' ? (
        <div className="business-card-grid two">
          <article className="business-card"><h2>Custom Sponsorships</h2><p>Premiere sponsorships, branded programming collections, title integrations, and larger cross-platform media packages are quoted individually.</p><a className="business-primary" href={`mailto:${ADVERTISING_EMAIL}?subject=WatchVIM%20Sponsorship%20Proposal`}>Request Sponsorship Proposal</a></article>
          <article className="business-card"><h2>Self-Service Video Ads</h2><p>For a faster launch, purchase 15- or 30-second pre-roll and mid-roll inventory with guaranteed impression goals.</p><NavLink to="/advertise/video">Create Video Campaign</NavLink></article>
        </div>
      ) : (
        <>
          <div className="business-card-grid three">
            <article className="business-card"><span className="business-kicker">Starting at $100</span><h2>15-Second Video Ads</h2><p>Reach viewers with a 5,000-impression Starter campaign. Choose pre-roll, mid-roll, or both.</p><NavLink className="business-primary" to="/advertise/video?duration=15&package=starter">Start a $100 Campaign</NavLink></article>
            <article className="business-card"><span className="business-kicker">Flexible pacing</span><h2>Daily Budgets</h2><p>Choose your daily spend and campaign length. WatchVIM calculates the corresponding daily impression cap from the campaign CPM.</p><NavLink to="/advertise/video?mode=daily&duration=15">Set a Daily Budget</NavLink></article>
            <article className="business-card"><span className="business-kicker">Measurable delivery</span><h2>Campaign Reporting</h2><p>Paid video campaigns include reporting for impressions, starts, completion rate, clicks, remaining delivery, and daily activity.</p><NavLink to="/advertise/dashboard">Open Campaign Report</NavLink></article>
          </div>
          <div className="business-card">
            <h2>Streaming inventory built for independent audiences</h2>
            <p>WatchVIM video campaigns are designed for eligible ad-supported viewing inventory. Advertisers can choose platform-wide placement or request title targeting, upload approved-ready creative, pay securely through PayPal, and track campaign delivery after approval.</p>
            <NavLink to="/advertise/pricing">See All Pricing</NavLink>
          </div>
        </>
      )}
    </BusinessShell>
  );
}

export function AdvertiseCampaignPage() {
  const { catalog } = useCatalog();
  const [params] = useSearchParams();
  const formRef = useRef(null);
  const paypalHostRef = useRef(null);
  const buttonsRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [campaignType, setCampaignType] = useState(params.get('duration') === '30' ? '30' : '15');
  const [billingMode, setBillingMode] = useState(params.get('mode') === 'daily' ? 'daily' : 'flat');
  const [packageKey, setPackageKey] = useState(params.get('package') || 'starter');
  const [dailyBudget, setDailyBudget] = useState('20');
  const [flightDays, setFlightDays] = useState('7');
  const [placementScope, setPlacementScope] = useState('platform');
  const [targetTitleId, setTargetTitleId] = useState('');
  const [status, setStatus] = useState('Loading secure campaign checkout…');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const paidOrderRef = useRef('');

  const offers = config?.advertiserOffers || FALLBACK_OFFERS;
  const publishedTitles = useMemo(() => (catalog?.titles || []).slice().sort((a, b) => a.title.localeCompare(b.title)), [catalog?.titles]);
  const targetTitle = publishedTitles.find((title) => title.id === targetTitleId) || null;
  const selectedPackage = (offers.flatPackages || FALLBACK_OFFERS.flatPackages).find((pkg) => pkg.key === packageKey) || (offers.flatPackages || FALLBACK_OFFERS.flatPackages)[0];
  const minDailyBudget = Number(offers.daily?.minBudget?.[campaignType] || (campaignType === '30' ? 30 : 20));
  const cpm = Number(offers.cpm?.[campaignType] || (campaignType === '30' ? 30 : 20));
  const safeDailyBudget = Math.max(minDailyBudget, Number(dailyBudget || 0));
  const safeDays = Math.min(30, Math.max(1, Number(flightDays || 1)));
  const dailyImpressions = Math.floor((safeDailyBudget / cpm) * 1000);
  const totalDailyImpressions = dailyImpressions * safeDays;
  const flatPrice = Number(selectedPackage?.prices?.[campaignType] || 0);
  const quotedAmount = billingMode === 'daily' ? safeDailyBudget * safeDays : flatPrice;
  const quotedImpressions = billingMode === 'daily' ? totalDailyImpressions : Number(selectedPackage?.impressions || 0);

  useEffect(() => {
    let active = true;
    fetchAdConfig().then((next) => {
      if (!active) return;
      setConfig(next);
      const min = Number(next?.advertiserOffers?.daily?.minBudget?.[campaignType] || (campaignType === '30' ? 30 : 20));
      setDailyBudget((current) => Number(current) < min ? String(min) : current);
    }).catch(() => {});
    return () => { active = false; };
  }, [campaignType]);

  function validateForm() {
    const form = formRef.current;
    if (!form) return 'Campaign form is unavailable.';
    const data = new FormData(form);
    for (const key of ['companyName', 'contactName', 'email', 'companyUrl']) {
      if (!String(data.get(key) || '').trim()) return 'Please complete all required company and contact fields.';
    }
    if (placementScope === 'title' && !targetTitleId) return 'Select the WatchVIM title this campaign should target.';
    if (billingMode === 'daily' && (!Number.isFinite(Number(dailyBudget)) || Number(dailyBudget) < minDailyBudget)) return `Daily budget must be at least ${money(minDailyBudget)} for a ${campaignType}-second campaign.`;
    if (billingMode === 'daily' && (!Number.isFinite(Number(flightDays)) || Number(flightDays) < 1 || Number(flightDays) > 30)) return 'Daily campaigns must run between 1 and 30 days.';
    const media = data.get('media');
    const logo = data.get('logo');
    if (!(media instanceof File) || !media.name) return 'Upload your video ad creative before checkout.';
    if (!(logo instanceof File) || !logo.name) return 'Upload your company logo before checkout.';
    return '';
  }

  useEffect(() => {
    if (!paypalHostRef.current) return undefined;
    let active = true;
    setError('');
    loadPayPalSdk({ intent: 'capture' }).then((paypal) => {
      if (!active || !paypalHostRef.current) return;
      try { buttonsRef.current?.close?.(); } catch (_) {}
      paypalHostRef.current.innerHTML = '';
      const buttons = paypal.Buttons({
        style: { shape: 'pill', label: 'paypal', height: 48 },
        createOrder: async () => {
          const problem = validateForm();
          if (problem) { setError(problem); throw new Error(problem); }
          setError(''); setSuccess(''); setReportUrl(''); setStatus('Creating secure WatchVIM advertising order…');
          const order = await createAdvertisingOrder({
            campaignType,
            billingMode,
            packageKey,
            dailyBudget: billingMode === 'daily' ? dailyBudget : undefined,
            flightDays: billingMode === 'daily' ? flightDays : undefined,
          });
          if (!order?.id) throw new Error('PayPal did not return an advertising order.');
          paidOrderRef.current = String(order.id);
          setStatus(`PayPal checkout · ${money(order.watchvim_amount)} · ${formatInt(order.watchvim_impression_goal)} impression goal`);
          return order.id;
        },
        onApprove: async (data) => {
          const orderId = String(data?.orderID || paidOrderRef.current || '').trim();
          if (!orderId) throw new Error('PayPal returned without an order reference.');
          setStatus('Capturing and verifying payment…');
          const captured = await captureAdvertisingOrder(orderId);
          if (String(captured?.status || '').toUpperCase() !== 'COMPLETED') throw new Error('PayPal has not completed this campaign payment.');
          const formData = new FormData(formRef.current);
          formData.set('campaignType', campaignType);
          formData.set('billingMode', billingMode);
          formData.set('packageKey', packageKey);
          formData.set('dailyBudget', billingMode === 'daily' ? dailyBudget : '');
          formData.set('flightDays', billingMode === 'daily' ? flightDays : '');
          formData.set('placementScope', placementScope);
          formData.set('targetTitleId', placementScope === 'title' ? targetTitleId : '');
          formData.set('targetTitleName', placementScope === 'title' ? (targetTitle?.title || '') : '');
          formData.set('orderId', orderId);
          setStatus('Payment captured. Submitting creative for WatchVIM approval…');
          try {
            const result = await submitAdvertisingCampaign(formData);
            setStatus('');
            setSuccess(`Campaign submitted for approval. Reference: ${result?.item?.id || orderId}. Your purchased impression goal begins delivering after approval.`);
            setReportUrl(result?.reportUrl || '');
            formRef.current?.reset();
            paidOrderRef.current = '';
          } catch (submitError) {
            setStatus('');
            setError(`Payment was captured, but campaign setup did not finish. Do not pay again. Keep PayPal order ${orderId} and contact WatchVIM support. ${submitError?.message || ''}`.trim());
          }
        },
        onCancel: () => setStatus('Checkout cancelled. No campaign was submitted.'),
        onError: (err) => { setStatus(''); setError(err?.message || 'PayPal advertising checkout could not be completed.'); },
      });
      buttonsRef.current = buttons;
      setStatus('');
      return buttons.render(paypalHostRef.current);
    }).catch((err) => { if (active) { setStatus(''); setError(err?.message || 'PayPal advertising checkout is unavailable.'); } });
    return () => { active = false; try { buttonsRef.current?.close?.(); } catch (_) {} };
  }, [campaignType, billingMode, packageKey, dailyBudget, flightDays, placementScope, targetTitleId, targetTitle?.title, minDailyBudget]);

  return (
    <BusinessShell eyebrow="WatchVIM for Brands" title="Build Your Video Campaign" subtitle="Choose guaranteed impressions or a daily budget for 15- or 30-second pre-roll and mid-roll advertising.">
      <BusinessNav />
      <div className="business-two-column">
        <form ref={formRef} className="business-form" onSubmit={(event) => event.preventDefault()}>
          <div className="business-field-row two">
            <label>Company name<input name="companyName" required /></label>
            <label>Contact name<input name="contactName" required /></label>
          </div>
          <div className="business-field-row two">
            <label>Email<input name="email" type="email" required /></label>
            <label>Company URL<input name="companyUrl" type="url" placeholder="https://" required /></label>
          </div>

          <div className="business-field-row two">
            <label>Video length<select name="campaignType" value={campaignType} onChange={(event) => setCampaignType(event.target.value)}><option value="15">15 seconds · base ${offers.cpm?.['15'] || '20'} CPM</option><option value="30">30 seconds · base ${offers.cpm?.['30'] || '30'} CPM</option></select></label>
            <label>Purchase type<select name="billingMode" value={billingMode} onChange={(event) => setBillingMode(event.target.value)}><option value="flat">Guaranteed impression package</option><option value="daily">Daily budget</option></select></label>
          </div>

          {billingMode === 'flat' ? (
            <label>Impression package<select name="packageKey" value={packageKey} onChange={(event) => setPackageKey(event.target.value)}>{(offers.flatPackages || FALLBACK_OFFERS.flatPackages).map((pkg) => <option key={pkg.key} value={pkg.key}>{pkg.label} · {formatInt(pkg.impressions)} impressions · {money(pkg.prices?.[campaignType])}</option>)}</select></label>
          ) : (
            <div className="business-field-row two">
              <label>Daily budget<input name="dailyBudget" type="number" min={minDailyBudget} max={Number(offers.daily?.maxBudget || 5000)} step="1" value={dailyBudget} onChange={(event) => setDailyBudget(event.target.value)} /></label>
              <label>Campaign days<input name="flightDays" type="number" min="1" max="30" step="1" value={flightDays} onChange={(event) => setFlightDays(event.target.value)} /></label>
            </div>
          )}

          <div className="business-field-row two">
            <label>Ad position<select name="adPosition" defaultValue="preroll"><option value="preroll">Pre-roll</option><option value="midroll">Mid-roll</option><option value="both">Pre-roll + Mid-roll</option></select></label>
            <label>Placement scope<select name="placementScope" value={placementScope} onChange={(event) => setPlacementScope(event.target.value)}><option value="platform">Platform-wide</option><option value="title">Target a published title</option></select></label>
          </div>
          {placementScope === 'title' ? <label>Target title<select value={targetTitleId} onChange={(event) => setTargetTitleId(event.target.value)} required><option value="">Select a title…</option>{publishedTitles.map((title) => <option key={title.id} value={title.id}>{title.title}</option>)}</select></label> : null}
          <label>Optional third-party VAST URL<input name="vastUrl" type="url" placeholder="https://" /></label>
          <label>{campaignType}-second video creative<input name="media" type="file" accept="video/mp4,video/quicktime" required /></label>
          <label>Company logo<input name="logo" type="file" accept="image/png,image/jpeg" required /></label>
          <p className="business-fineprint">Creative is reviewed before delivery. Upload a final ad matching the selected 15- or 30-second length. Eligible inventory may include WatchVIM web, mobile, tablet, and supported TV/OTT applications as placements are enabled.</p>
          <StatusMessage error={error} success={success} status={status} />
          {reportUrl ? <p><NavLink className="business-primary" to={reportUrl}>Save / Open Campaign Report</NavLink></p> : null}
          <div ref={paypalHostRef} className="paypal-button-host" aria-label="PayPal advertising checkout" />
          <p className="business-fineprint">Payment is verified server-side. Purchased impressions begin only after WatchVIM approves the creative and campaign.</p>
        </form>

        <aside className="business-card sticky-card">
          <span className="business-kicker">Campaign estimate</span>
          <h2>{campaignType}-second video ad</h2>
          <strong className="business-price">{money(quotedAmount)}</strong>
          <p><strong>{formatInt(quotedImpressions)}</strong> purchased impressions</p>
          {billingMode === 'daily' ? <p>{money(safeDailyBudget)}/day · up to {formatInt(dailyImpressions)} impressions/day · {safeDays} day{safeDays === 1 ? '' : 's'}</p> : <p>{selectedPackage?.label} package · one flat campaign price</p>}
          <p>Base CPM: {money(cpm)}</p>
          <p>Pre-roll, mid-roll, or both · platform-wide or title-targeted review.</p>
        </aside>
      </div>
    </BusinessShell>
  );
}

function Metric({ label, value, note }) {
  return <article className="business-card"><span className="business-kicker">{label}</span><h2>{value}</h2>{note ? <p className="business-muted">{note}</p> : null}</article>;
}

export function AdvertiserDashboardPage() {
  const [params, setParams] = useSearchParams();
  const [tokenInput, setTokenInput] = useState(params.get('token') || '');
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  async function load(token = tokenInput) {
    const clean = String(token || '').trim();
    if (!clean) { setError('Enter the private reporting token from your WatchVIM campaign confirmation.'); return; }
    setError(''); setStatus('Loading campaign delivery…'); setReport(null);
    try {
      const result = await fetchAdvertiserReport(clean);
      setReport(result.report || null);
      setStatus('');
      setParams({ token: clean }, { replace: true });
    } catch (err) {
      setStatus(''); setError(err?.message || 'Campaign report could not be loaded.');
    }
  }

  useEffect(() => {
    const initial = params.get('token');
    if (initial) load(initial);
    // Only auto-load the token present at page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BusinessShell eyebrow="WatchVIM for Brands" title="Campaign Performance" subtitle="Track purchased impressions, delivery progress, video completions, clicks, and daily campaign activity.">
      <BusinessNav />
      <div className="business-form wide">
        <label>Private campaign reporting token<input value={tokenInput} onChange={(event) => setTokenInput(event.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></label>
        <button className="business-primary button" type="button" onClick={() => load()}>Load Campaign Report</button>
        <StatusMessage error={error} status={status} />
      </div>

      {report ? (
        <>
          <div className="business-card">
            <span className="business-kicker">{report.companyName}</span>
            <h2>{report.durationSeconds}-second campaign · {String(report.status || '').toUpperCase()}</h2>
            <p>{report.billingMode === 'daily' ? `${money(report.dailyBudget)}/day · ${report.flightDays} days` : `${String(report.packageKey || 'flat')} impression package`} · {report.adPosition} · {report.placementScope === 'title' ? (report.targetTitleName || 'title targeted') : 'platform-wide'}</p>
          </div>
          <div className="business-card-grid four">
            <Metric label="Delivered" value={formatInt(report.impressionsDelivered)} note={`${report.deliveryPercent}% of ${formatInt(report.impressionGoal)} purchased`} />
            <Metric label="Remaining" value={formatInt(report.impressionsRemaining)} note="Campaign stops automatically at its purchased goal" />
            <Metric label="Completion rate" value={`${Number(report.completionRate || 0).toFixed(1)}%`} note={`${formatInt(report.totals?.completes)} completed video ads`} />
            <Metric label="Clicks" value={formatInt(report.totals?.clicks)} note={`${Number(report.clickThroughRate || 0).toFixed(2)}% CTR`} />
          </div>
          <div className="business-card-grid three">
            <Metric label="Starts" value={formatInt(report.totals?.starts)} />
            <Metric label="Total budget" value={money(report.totalBudget)} note={`${money(report.cpm)} CPM`} />
            <Metric label="Today's delivery" value={formatInt(report.currentDailyImpressions)} note={report.dailyImpressionCap ? `${formatInt(report.dailyImpressionCap)} daily cap` : 'No daily cap'} />
          </div>
          <div className="business-card">
            <h2>Daily Delivery</h2>
            {(report.daily || []).length ? (
              <div className="business-table-wrap">
                <table className="business-table">
                  <thead><tr><th>Date</th><th>Impressions</th><th>Starts</th><th>Completes</th><th>Clicks</th></tr></thead>
                  <tbody>{report.daily.map((row) => <tr key={row.date}><td>{row.date}</td><td>{formatInt(row.impressions)}</td><td>{formatInt(row.starts)}</td><td>{formatInt(row.completes)}</td><td>{formatInt(row.clicks)}</td></tr>)}</tbody>
                </table>
              </div>
            ) : <p className="business-muted">Delivery data will appear after the campaign is approved and begins serving.</p>}
          </div>
        </>
      ) : null}
    </BusinessShell>
  );
}
